import * as radixColors from '@radix-ui/colors';

import * as aiColor from './ai';
import * as complyanceColor from './complyance';

export type ColorScale = { [key: number]: string };
export type RadixColorScale = { [key: `${string}${number}`]: string };

// List of colors we want to support
export const colorNames = [
  'complyance',
  'ai',
  'gray',
  'yellow',
  'orange',
  'red',
  'pink',
  'purple',
  'indigo',
  'blue',
  'cyan',
  'teal',
  'green',
  'brown',
] as const;

export type ColorKey = (typeof colorNames)[number] | 'accent' | 'neutral';
export type ColorEntry = {
  regular: ColorScale;
  dark: ColorScale;
  alpha: ColorScale;
  darkAlpha: ColorScale;
};

const colors = {
  ...complyanceColor,
  ...aiColor,
  ...radixColors,
};

export const extractRadixColors = (colorObj: RadixColorScale): ColorScale =>
  Object.fromEntries(
    Object.entries(colorObj)
      .filter(([key]) => /\d+$/.test(key))
      .map(([key, value]) => [parseInt(key.replace(/^\D+/, ''), 10), value]),
  );

// Generate colorScales dynamically
/**
 * Which concrete scale each alias resolves to. `accent` is the brand ramp and
 * `neutral` the grey, so changing the product's palette is a change here
 * rather than at every reference.
 *
 * Defined with the scales rather than in the theme generator, because
 * consumers resolve aliases too: the Chakra theme reads `neutral` to align its
 * own grey ramp during the migration.
 */
export const scaleAliases = { accent: 'complyance', neutral: 'gray' } as const;

const buildEntry = (color: string): ColorEntry => ({
  regular: extractRadixColors(colors[`${color}` as keyof typeof colors]),
  dark: extractRadixColors(colors[`${color}Dark` as keyof typeof colors]),
  alpha: extractRadixColors(colors[`${color}A` as keyof typeof colors]),
  darkAlpha: extractRadixColors(colors[`${color}DarkA` as keyof typeof colors]),
});

/* The aliases are materialised alongside the named scales. `ColorKey` has
 * always included them, so omitting them from the data made the type a
 * promise the runtime did not keep: `colorScales.neutral` typechecked and was
 * undefined. */
export const colorScales: Record<ColorKey, ColorEntry> = Object.fromEntries([
  ...colorNames.map((color) => [color, buildEntry(color)]),
  ...Object.entries(scaleAliases).map(([alias, target]) => [alias, buildEntry(target)]),
]) as Record<ColorKey, ColorEntry>;

/**
 * The pure poles. Radix scales deliberately start one step in from pure white
 * and pure black (gray-1 is #fcfcfc), so a surface that should read as
 * genuinely white in light mode has no step to reach for. These name that
 * value once instead of letting a literal appear at the point of use.
 *
 * They are poles, not scale steps: nothing derives from them and they carry
 * no dark-mode counterpart, because "the lightest possible surface" is white
 * in both themes even though only the light theme has a use for it.
 */
export const poles = {
  white: '#ffffff',
  black: '#000000',
} as const;

/**
 * Semantic color roles, resolved to token scales by the theme generator
 * (solid = step 9, bg = step 3, border = step 7, text = step 11).
 */
export const semanticColors = {
  /* The agent's voice. A fifth role rather than a label color because it
   * carries meaning (this came from the AI, or will go to it), and its own
   * scale rather than a Radix hue because the product designed the exact
   * violet-plum; see ./ai.ts for the reservation rule. */
  ai: 'ai',
  success: 'green',
  /* Orange, not yellow. Yellow's step 9 is too light to carry a solid, and
   * the product's own usage had already drifted there: the same "expiring"
   * state mapped to orange in eight status maps and yellow in one. Orange is
   * what warning meant in practice; the token now says so. */
  warning: 'orange',
  error: 'red',
  info: 'blue',
} as const;

export type SemanticColor = keyof typeof semanticColors;

/**
 * The closed set of label colors: decorative hues for things USERS attach
 * (tags, categories), as opposed to the semantic roles above, which the
 * SYSTEM computes. A label color carries no meaning, so the set can be
 * bigger than the semantic five and can include hues (yellow, teal, cyan,
 * brown) that semantics deliberately avoids.
 *
 * Components resolve a label color to the Radix soft-badge anatomy: step 3
 * background (alpha, so it composes on any surface), step 11 text, step 6/7
 * border. Dark mode follows from the Radix dark scales by construction.
 *
 * Auto-assignment hashes the label text onto the first ELEVEN entries; gray
 * is excluded from the hash and reserved as an intentional mute a user picks
 * on purpose. See docs/specs/frontend/design-system/badge-status-tag.md.
 */
export const labelColors = [
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'cyan',
  'blue',
  'indigo',
  'purple',
  'pink',
  'brown',
  'gray',
] as const;

export type LabelColor = (typeof labelColors)[number];
